// Phase 4 detector, built from the real Biotron data: notes arrive at a fixed clock (~7.7/s) whatever you do,
// and velocity is spread wide (15-90) in every window, so neither carries touch information. CC90 sits on
// plateaus with occasional isolated jumps. The only signal-dependent quantity is how much CC90 (and the pitch
// derived from it) keeps MOVING. So the core signal is one ACTIVITY ENVELOPE (0-1): sustained CC90 + pitch
// motion relative to a rolling robust baseline. Isolated jumps are rejected by a median-of-3 on the deltas.
export const TOUCH_DEFAULTS = {
  ccNumber: 90,
  sensitivity: 3,
  thresholdScale: 1,
  frameMs: 100,
  windowS: 30,
  recomputeMs: 500,
  motionWindowMs: 600,
  jumpClip: 30,
  pitchClip: 8,
  pitchWeight: 1,
  floor: 8,
  attackMs: 250,
  releaseMs: 450,
  bands: { LOW: 0.15, MID: 0.38, HIGH: 0.68 },
  bandHysteresis: 0.05,
  bandDwellMs: 400,
  touchOn: 0.2,
  touchOff: 0.1,
  touchReleaseMs: 200,
  rebaselineAfterMs: 25000,
  stableRatio: 1.6,
  stillThresholdsS: [3, 6, 10],
  warmupMs: 2500,
};
// Sensitivity 1 (least) .. 5 (most): raw motion above baseline (in robust spreads) that maps to activity 1.0.
export const SENSITIVITY_LEVELS = { 1: 8, 2: 6, 3: 4.8, 4: 3.6, 5: 2.7 };
export const BAND_ORDER = ['NONE', 'LOW', 'MID', 'HIGH'];
export const DEFAULT_BASELINE = { median: { raw: 2, level: 0 }, mad: { raw: 0 }, ccMean: 0, ccStd: 3, rate: 0, learnedAt: null, rebaselines: 0, sinceT: null };

const median = (a) => {
  if (!a.length) return 0;
  const s = [...a].sort((x, y) => x - y);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const mad = (a, m) => median(a.map((x) => Math.abs(x - m)));
const clamp01 = (x) => Math.max(0, Math.min(1, x));

export function createTouchEngine(config = {}, { baseline = DEFAULT_BASELINE, startT = 0 } = {}) {
  let cfg = { ...TOUCH_DEFAULTS, ...config };
  let base = { ...DEFAULT_BASELINE, ...baseline, sinceT: startT };
  const listeners = new Set();
  let motion = [];
  let lastCc = null;
  let lastPitch = null;
  let ccDeltas = [0, 0];
  let pDeltas = [0, 0];
  let lastT = startT;
  let lastFrameT = startT;
  let lastRecompute = startT;
  let allFrames = [];
  let quietFrames = [];
  let activity = 0;
  let band = 'NONE';
  let bandCand = 'NONE';
  let bandCandSince = startT;
  let touch = null;
  let belowSince = null;
  let lastTouchEnd = startT;
  let stillFired = new Set();
  let learn = null;
  let activeSince = null;
  const warmUntil = startT + cfg.warmupMs;
  let feat = { raw: 0, ccMotion: 0, pitchMotion: 0, target: 0 };
  const emit = (type, t, extra = {}) => listeners.forEach((fn) => fn({ type, t, ...extra }));
  const spread = () => Math.max(1.4826 * base.mad.raw, cfg.floor);
  const full = () => (SENSITIVITY_LEVELS[cfg.sensitivity] || 8) * cfg.thresholdScale;

  function ingest(e) {
    if (learn) learn.events.push(e);
    if (e.type === 'cc' && e.cc === cfg.ccNumber) {
      if (lastCc !== null) {
        ccDeltas = [ccDeltas[1], Math.min(cfg.jumpClip, Math.abs(e.value - lastCc))];
        // min of the last two deltas: an isolated plateau jump contributes 0, sustained motion passes
        motion.push({ t: e.t, cc: Math.min(ccDeltas[0], ccDeltas[1]), p: 0 });
      }
      lastCc = e.value;
    } else if (e.type === 'note_on') {
      if (lastPitch !== null) {
        pDeltas = [pDeltas[1], Math.min(cfg.pitchClip, Math.abs(e.note - lastPitch))];
        motion.push({ t: e.t, cc: 0, p: Math.min(pDeltas[0], pDeltas[1]) });
      }
      lastPitch = e.note;
    }
  }

  function recompute(t) {
    const keep = (cfg.windowS * 1000) / cfg.frameMs;
    if (allFrames.length > keep) allFrames = allFrames.slice(-keep);
    if (quietFrames.length > keep) quietFrames = quietFrames.slice(-keep);
    const src = quietFrames.length >= 20 ? quietFrames : allFrames;
    if (!src.length) return;
    const raw = src.map((f) => f.raw);
    const m = median(raw);
    const lv = src.slice(-80).map((f) => f.level).filter((x) => x !== null);
    base.median = { raw: m, level: median(lv) };
    base.mad = { raw: mad(raw, m) };
    base.ccMean = base.median.level;
    base.ccStd = Math.max(1.4826 * mad(lv, base.median.level), 3);
    lastRecompute = t;
  }

  function setBand(next, t) {
    if (next === band) return;
    band = next;
    emit('BAND', t, { band });
  }

  function rebaseline(t, reason) {
    const recent = allFrames.slice(-Math.round(10000 / cfg.frameMs));
    allFrames = recent.slice();
    quietFrames = recent.slice();
    recompute(t);
    base.rebaselines += 1;
    base.sinceT = t;
    base.learnedAt = new Date().toISOString();
    activity = 0;
    setBand('NONE', t);
    if (touch) endTouch(t, reason);
    emit('REBASELINE', t, { reason, baseline: getBaseline() });
  }

  function endTouch(t, reason) {
    const dur = (belowSince ?? t) - touch.start;
    emit('TOUCH_END', t, { durationMs: Math.round(dur), peak: Math.round(touch.peak * 100) / 100, reason });
    touch = null;
    belowSince = null;
    lastTouchEnd = t;
    stillFired = new Set();
  }

  function tick(t) {
    const dt = Math.max(0, t - lastT);
    lastT = t;
    while (motion.length && motion[0].t < t - cfg.motionWindowMs) motion.shift();
    const ccMotion = motion.reduce((s, x) => s + x.cc, 0);
    const pitchMotion = motion.reduce((s, x) => s + x.p, 0);
    const raw = ccMotion + cfg.pitchWeight * pitchMotion;
    const target = clamp01((raw - base.median.raw) / (spread() * full()));
    feat = { raw, ccMotion, pitchMotion, target };
    if (t - lastFrameT >= cfg.frameMs) {
      lastFrameT = t;
      const f = { raw, level: lastCc };
      allFrames.push(f);
      if (band === 'NONE' || learn) quietFrames.push(f);
    }
    if (t - lastRecompute >= cfg.recomputeMs) recompute(t);
    if (learn) {
      activity = 0;
      if (t - learn.start >= learn.ms) {
        const recent = allFrames.slice(-Math.round(learn.ms / cfg.frameMs));
        allFrames = recent.slice();
        quietFrames = recent.slice();
        recompute(t);
        base.learnedAt = new Date().toISOString();
        base.sinceT = t;
        const events = learn.events;
        learn = null;
        emit('BASELINE_LEARNED', t, { baseline: getBaseline(), events });
      }
      return getState();
    }
    if (t < warmUntil) return getState();
    const tau = target > activity ? cfg.attackMs : cfg.releaseMs;
    activity += (target - activity) * (1 - Math.exp(-dt / tau));
    // band: hysteresis + minimum dwell
    const h = cfg.bandHysteresis;
    const cur = BAND_ORDER.indexOf(band);
    let want = 'NONE';
    ['LOW', 'MID', 'HIGH'].forEach((b, i) => {
      if (activity >= cfg.bands[b] + (i + 1 <= cur ? -h : h)) want = b;
    });
    if (want !== bandCand) {
      bandCand = want;
      bandCandSince = t;
    }
    if (bandCand !== band && t - bandCandSince >= cfg.bandDwellMs) setBand(bandCand, t);
    // touch on/off from the unsmoothed target (faster, so separate jabs stay separate)
    if (!touch) {
      if (target >= cfg.touchOn) {
        touch = { start: t, peak: activity };
        emit('TOUCH_START', t, { activity });
      }
    } else {
      touch.peak = Math.max(touch.peak, activity);
      if (target < cfg.touchOff) {
        if (belowSince === null) belowSince = t;
        if (t - belowSince >= cfg.touchReleaseMs) endTouch(t, 'release');
      } else belowSince = null;
    }
    if (band === 'NONE') activeSince = null;
    else activeSince = activeSince ?? t;
    {
      // band stuck above NONE for ~25 s with stable statistics -> it is the new background
      if (activeSince !== null && t - activeSince >= cfg.rebaselineAfterMs) {
        const fr = allFrames.slice(-100).map((f) => f.raw);
        const a = median(fr.slice(0, 50));
        const b = median(fr.slice(50));
        if (fr.length >= 100 && Math.max(a, b) / Math.max(1, Math.min(a, b)) <= cfg.stableRatio) {
          activeSince = null;
          rebaseline(t, 'stuck-activity');
        }
      }
    }
    const still = touch ? 0 : (t - lastTouchEnd) / 1000;
    cfg.stillThresholdsS.forEach((x) => {
      if (!touch && still >= x && !stillFired.has(x)) {
        stillFired.add(x);
        emit('STILL', t, { seconds: x });
      }
    });
    return getState();
  }

  function getBaseline() {
    return { ...base, median: { ...base.median }, mad: { ...base.mad }, spread: spread(), ageS: base.sinceT === null ? 0 : (lastT - base.sinceT) / 1000 };
  }
  function getState() {
    return {
      activity, band, touchActive: !!touch, touchMs: touch ? lastT - touch.start : 0, score: activity,
      stillnessSeconds: touch ? 0 : (lastT - lastTouchEnd) / 1000, features: { ...feat },
      learning: !!learn, learnRemainingMs: learn ? Math.max(0, learn.ms - (lastT - learn.start)) : 0,
      baseline: getBaseline(), thresholds: { on: cfg.touchOn, off: cfg.touchOff, bands: { ...cfg.bands }, full: full() },
    };
  }

  return {
    ingest,
    tick,
    getState,
    learnBaseline(ms, t = lastT) {
      learn = { start: t, ms, events: [] };
      if (touch) endTouch(t, 'relearn');
      setBand('NONE', t);
    },
    getBaseline,
    setBaseline(b) {
      base = { ...DEFAULT_BASELINE, ...b, median: { ...DEFAULT_BASELINE.median, ...(b.median || {}) }, mad: { ...DEFAULT_BASELINE.mad, ...(b.mad || {}) }, sinceT: lastT };
      if (typeof base.median.raw !== 'number' || Number.isNaN(base.median.raw)) base.median.raw = 2;
      if (typeof base.mad.raw !== 'number' || Number.isNaN(base.mad.raw)) base.mad.raw = 0;
    },
    getConfig: () => ({ ...cfg }),
    setConfig(partial) {
      cfg = { ...cfg, ...partial };
    },
    on(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}
